/**
 * /app/notifications — wired against `list-notifications`.
 *
 * MVP renders the inbox read-only. Mark-read / mark-dismissed mutations
 * are deferred; the action buttons surface the "coming soon" state per
 * `docs/frontend/035-frontend-app-shell-plan.md §3.5`.
 */

import { useEffect, useState } from 'react';
import { isApiError } from '../api/client';
import { listNotifications } from '../api/endpoints';
import type { ListNotificationsData, NotificationRow } from '../api/types';
import { EmptyState, ErrorState, LoadingState } from '../components/states';

interface Page {
  readonly rows: ReadonlyArray<NotificationRow>;
  readonly nextBefore?: string;
  readonly nextBeforeId?: string;
}

type LoadState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly page: Page; readonly loadingMore: boolean }
  | { readonly status: 'error'; readonly error: unknown };

export function NotificationsPage() {
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const ctrl = new AbortController();
    setState({ status: 'loading' });
    listNotifications({ limit: 20, unreadOnly, signal: ctrl.signal })
      .then((data: ListNotificationsData) => {
        if (ctrl.signal.aborted) return;
        setState({
          status: 'ready',
          loadingMore: false,
          page: {
            rows: data.notifications,
            nextBefore: data.next_before,
            nextBeforeId: data.next_before_id,
          },
        });
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setState({ status: 'error', error: err });
      });
    return () => ctrl.abort();
  }, [unreadOnly, reloadKey]);

  async function loadMore() {
    if (state.status !== 'ready') return;
    if (!state.page.nextBefore || !state.page.nextBeforeId) return;
    setState((prev) => (prev.status === 'ready' ? { ...prev, loadingMore: true } : prev));
    try {
      const data = await listNotifications({
        limit: 20,
        unreadOnly,
        before: state.page.nextBefore,
        beforeId: state.page.nextBeforeId,
      });
      setState((prev) =>
        prev.status === 'ready'
          ? {
              status: 'ready',
              loadingMore: false,
              page: {
                rows: [...prev.page.rows, ...data.notifications],
                nextBefore: data.next_before,
                nextBeforeId: data.next_before_id,
              },
            }
          : prev,
      );
    } catch (err) {
      if (isApiError(err) && err.code === 'unauthenticated') return;
      setState((prev) => (prev.status === 'ready' ? { ...prev, loadingMore: false } : prev));
    }
  }

  return (
    <div className="stack-md">
      <div className="page-head">
        <div>
          <h1 className="h-page">
            الإشعارات{' '}
            {state.status === 'ready' ? (
              <span
                className="serif italic"
                style={{ color: 'var(--tangerine)', fontSize: '0.6em' }}
              >
                — {state.page.rows.length} عنصر
              </span>
            ) : null}
          </h1>
          <p className="sub">
            صندوق الإشعارات للحساب الحالي. mark-read / dismiss سيُربطان في مهمة لاحقة.
          </p>
        </div>
      </div>

      <div className="toolbar-row">
        <button
          type="button"
          className={!unreadOnly ? 'chip on' : 'chip'}
          onClick={() => setUnreadOnly(false)}
        >
          الكل
        </button>
        <button
          type="button"
          className={unreadOnly ? 'chip on' : 'chip'}
          onClick={() => setUnreadOnly(true)}
        >
          غير مقروء فقط
        </button>
      </div>

      {state.status === 'loading' ? <LoadingState title="جاري تحميل الإشعارات…" /> : null}
      {state.status === 'error' ? (
        <ErrorState
          title="تعذر تحميل الإشعارات"
          error={state.error}
          onRetry={() => setReloadKey((n) => n + 1)}
        />
      ) : null}
      {state.status === 'ready' ? (
        state.page.rows.length === 0 ? (
          <EmptyState
            title={unreadOnly ? 'لا يوجد إشعار غير مقروء' : 'لا توجد إشعارات'}
            description="ستظهر هنا الإشعارات التي ينتجها النظام (publishing / approvals / billing / supervised access)."
          />
        ) : (
          <>
            <ul
              style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              {state.page.rows.map((n) => (
                <li
                  key={n.id}
                  className="card"
                  style={{
                    padding: 16,
                    background: n.read_at ? 'var(--paper)' : 'var(--paper-2)',
                    display: 'flex',
                    gap: 12,
                  }}
                >
                  <span
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: '50%',
                      background:
                        n.priority === 'urgent' || n.priority === 'high'
                          ? 'var(--rose-soft)'
                          : 'var(--cream-2)',
                      color:
                        n.priority === 'urgent' || n.priority === 'high'
                          ? 'var(--rose-ink)'
                          : 'var(--ink-2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      fontFamily: 'var(--font-serif)',
                      fontWeight: 600,
                    }}
                  >
                    {n.notification_type.charAt(0).toUpperCase()}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}
                    >
                      <strong style={{ fontSize: 13 }}>{n.title}</strong>
                      <span className="pill pill-info" style={{ fontSize: 10 }}>
                        {n.notification_type}
                      </span>
                      {n.priority !== 'normal' ? (
                        <span
                          className={
                            n.priority === 'urgent' || n.priority === 'high'
                              ? 'pill pill-error'
                              : 'pill pill-info'
                          }
                          style={{ fontSize: 10 }}
                        >
                          {n.priority}
                        </span>
                      ) : null}
                    </div>
                    {n.body ? (
                      <p className="body" style={{ marginTop: 6 }}>
                        {n.body}
                      </p>
                    ) : null}
                    <div className="tiny" style={{ color: 'var(--muted)', marginTop: 6 }}>
                      {new Date(n.created_at).toLocaleString('ar-SA')}
                    </div>
                  </div>
                  {!n.read_at ? <span className="badge-dot" /> : null}
                </li>
              ))}
            </ul>
            {state.page.nextBefore && state.page.nextBeforeId ? (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    void loadMore();
                  }}
                  disabled={state.loadingMore}
                >
                  {state.loadingMore ? 'جاري التحميل…' : 'تحميل المزيد'}
                </button>
              </div>
            ) : null}
          </>
        )
      ) : null}
    </div>
  );
}
